import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { trackNavDropdownSelection, trackNavDropdownToggle } from '../../analytics/analytics';

export interface DropdownItem {
  label: string;
  to?: string;
  onSelect?: () => void;
  disabled?: boolean;
  primary?: boolean;
}

interface DropdownProps {
  label: string;
  items: DropdownItem[];
  className?: string;
  menuClassName?: string;
  itemClassName?: string;
}

const Dropdown: React.FC<DropdownProps> = ({ label, items, className, menuClassName, itemClassName }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const next = !isOpen;
      setIsOpen(next);
      trackNavDropdownToggle(label, next);
    } else if (event.key === 'Escape') {
      setIsOpen(false);
      trackNavDropdownToggle(label, false);
    }
  };
  return (
    <div className={`dropdown ${className ?? ''}`} ref={dropdownRef}>
      <button
        className="dropdown-toggle"
        onClick={() => {
          const next = !isOpen;
          setIsOpen(next);
          trackNavDropdownToggle(label, next);
        }}
        onKeyDown={handleKeyDown}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        type="button"
      >
        {label} ▼
      </button>
      {isOpen && (
        <ul className={`dropdown-menu ${menuClassName ?? ''}`} role="menu">
          {items.map((item, index) => {
            const itemClass = `dropdown-item${item.primary ? ' dropdown-item--primary' : ''}${item.disabled ? ' dropdown-item--disabled' : ''}${itemClassName ? ` ${itemClassName}` : ''}`;
            const handleSelection = () => {
              if (item.disabled) return;
              trackNavDropdownSelection(label, item.label);
              item.onSelect?.();
              setIsOpen(false);
            };

            return (
              <li key={`${item.label}-${index}`} role="none">
                {item.to && !item.disabled ? (
                  <Link
                    to={item.to}
                    className={itemClass}
                    role="menuitem"
                    onClick={handleSelection}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className={itemClass}
                    role="menuitem"
                    onClick={handleSelection}
                    disabled={item.disabled}
                  >
                    {item.label}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default Dropdown;